import {h, Fragment} from "preact"
import { useFacets, useAppStateSelector, pick, useProductFilters } from "@nosto/preact"
import FacetItemWrapper from "./FacetItemWrapper"
import RangeFacet from "./RangeFacet"
import SelectedFilters from "./SelectedFilters"
import { useEffect, useRef, useState } from "preact/hooks"
import { MobileSortBlock, Icon } from "./elements"
import { settings } from "../config"

export const toggleButtonId = "toggle-mobile-sidebar"

function ToggleSidebarButton({ className }) {
    return (
        <label
            className={`ns-act-btn ns-close-sidebar ns-absolute ns-clickable ${className}`} 
            for={toggleButtonId}>
            <Icon name="close" />
        </label>
    )
}

export default function SideBar() {
    const { facets } = useFacets()
    const { filters: selectedFilters, removeAll } = useProductFilters()

    const [displayCount, setDisplayCount] = useState(selectedFilters?.length || 0);

    useEffect(() => {
        const newCount = selectedFilters?.length || 0;
        setDisplayCount(newCount);
        // console.log("Display count updated to:", newCount, "Filters:", selectedFilters);
    }, [selectedFilters]);

    const selectedCount = displayCount;

    const inputRef = useRef(null)
    const isNewLayout = !!window.isNewLayout

    const { query, response, pageType } = useAppStateSelector(state =>
        pick(state, "loading", "query", "response", "pageType")
    )

    const docCount = response.products?.total ?? 0
    
    const baseText = pageType === "category" ? `${docCount}` : `'${query.query}' - ${docCount}`
    const unit = isNewLayout ? "products" : "results"

    const totalSummary = `${baseText} ${unit}`

    const collectionRelated = window.collectionRelated?.[0]?.collection_items || []
    const collectionVisual   = window.collectionVisual?.[0]?.collection_items   || []

    const swiperRef = useRef(null)

    useEffect(() => {
        if (swiperRef.current && collectionVisual.length >= 3) {
            new Swiper(swiperRef.current, {
                slidesPerView: 2.25,
                spaceBetween: 8,
                centeredSlides: false,
                loop: true,
                pagination: { el: '.swiper-pagination', clickable: true },
                navigation: {
                    nextEl: '.swiper-button-prev',
                    prevEl: '.swiper-button-next',
                }
            })
        }
    }, [collectionVisual])

    useEffect(() => {
        const listener = () => {
            if (window.innerWidth <= 1024 && inputRef.current?.checked) {
                return
            }

            if (window.innerWidth > 1024 && inputRef.current && !isNewLayout) {
                inputRef.current.checked = true
            } else if (window.innerWidth <= 1024 && inputRef.current) {
                inputRef.current.checked = false
            }
        }

        window.addEventListener('resize', listener)
        listener()

        return () => {
            window.removeEventListener('resize', listener)
        }
    }, [])

    useEffect(() => {
        if (!isNewLayout) return;
    
        const checkbox = inputRef.current;
        if (!checkbox) return;
    
        const handleToggle = () => {
            if (checkbox.checked) {
                document.body.classList.add('overflow-hidden');
            } else {
                document.body.classList.remove('overflow-hidden');
            }
        };
    
        checkbox.addEventListener('change', handleToggle);
    
        return () => {
            checkbox.removeEventListener('change', handleToggle);
            document.body.classList.remove('overflow-hidden');
        };
    }, []);

    const siblingCollections = settings.sibling_collections && Array.isArray(settings.sibling_collections) ? settings.sibling_collections : []
    const currentUrl = window.location.pathname

    return facets?.length > 0 ? (
        <>
            <input ref={inputRef} type="checkbox" id={toggleButtonId} class="ns-facet-sidebar-toggle ns-d-none" />
            <label
                class="ns-sidebar-backdrop ns-fixed ns-w-100 ns-h-100 ns-background-black ns-d-none"
                for={toggleButtonId}
            />
            <div class={`ns-sidebar-wrapper ns-d-none ns-d-l-block ns-h-100 ns-col-3 
                ${isNewLayout ? 'ns-new-layout-sidebar' : ''}`}>
                <div class="ns-sidebar-content ns-relative ns-h-100 ns-mr-0 ns-mr-l-auto">
                    <div class="mobile-facets__header page-width ns-only-mobile">
                        <div class="mobile-facets__header-inner">
                            <span 
                                key={`mobile-heading-${selectedCount}-${isNewLayout}`}
                                class="mobile-facets__heading font-primary heading-1-md large-up-hide"
                            >
                                {isNewLayout 
                                    ? `Filter & Sort${selectedCount > 0 ? ` (${selectedCount})` : ''}`
                                    : "Filter & Sort"
                                }
                            </span>
                            <ToggleSidebarButton />
                        </div>
                    </div>
                    {isNewLayout && (
                        <>
                            {collectionVisual.length > 0 && (
                                <div class={`visual-carousel ns-relative ${collectionVisual.length < 3 ? 'visual-few-items' : 'visual-many-items'}`}>
                                    {collectionVisual.length < 3 ? (
                                        <div class="visual-static-grid">
                                            {collectionVisual.map((slide, index) => (
                                                <a
                                                    key={index}
                                                    href={slide.link_url || '#'}
                                                    class="block relative visual-link"
                                                >
                                                    <div class="visual-image">
                                                        <img 
                                                            src={slide.image} 
                                                            alt={slide.heading || slide.title}
                                                            loading="lazy" 
                                                        />
                                                    </div>
                                                    <div class="visual-info">
                                                        <h3 class="heading">{slide.heading || slide.title}</h3>
                                                        <p class="subheading">{slide.subheading}</p>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <div class="swiper" ref={swiperRef}>
                                            <div class="swiper-wrapper">
                                                {collectionVisual.map((slide, index) => (
                                                    <div key={index} class="swiper-slide">
                                                        <a
                                                            href={slide.link_url || '#'}
                                                            class="block relative visual-link"
                                                        >
                                                            <div class="visual-image">
                                                                <img 
                                                                    src={slide.image} 
                                                                    alt={slide.heading || slide.title}
                                                                    loading="lazy" 
                                                                />
                                                            </div>
                                                            <div class="visual-info">
                                                                <h3 class="heading">{slide.heading || slide.title}</h3>
                                                                <p class="subheading">{slide.subheading}</p>
                                                            </div>
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>

                                            <div class="swiper-pagination"></div>
                                            <div class="swiper-button-prev"></div>
                                            <div class="swiper-button-next"></div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {collectionRelated.length > 0 && (
                                <div class="gender-visual-tabs">
                                    <div class="gender-wrapper">
                                        {collectionRelated.map((item, index) => {
                                            const isActive = item.is_active || window.location.pathname === item.link_url
                                            return (
                                                <a
                                                    key={index}
                                                    href={item.link_url}
                                                    class={`tab-button ${isActive ? 'active' : 'non-active'}`}
                                                    aria-selected={isActive ? 'true' : 'false'}
                                                >
                                                    {item.title.toUpperCase()}
                                                </a>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div class="ns-sidebar-menu">
                        {siblingCollections.length ? (
                            <div role="tabList" class="gender-filter ns-only-desktop">
                                {siblingCollections.map(item => (
                                    <a 
                                        class="tab-button font-primary nav" 
                                        href={item.url} 
                                        aria-selected={currentUrl === item.url ? 'true' : 'false'}>
                                            {item.name}
                                    </a>
                                ))}
                            </div>
                        ) : null}
                        <SelectedFilters />
                        <ul class="ns-list-unstyled ns-p-0 ns-m-0">
                            <MobileSortBlock />
                            {facets?.map(facet => {
                                switch (facet.type) {
                                    case "terms":
                                        return <FacetItemWrapper key={facet.id} facet={facet} />
                                    case "stats":
                                        return <RangeFacet key={facet.id} facet={facet} />
                                    default:
                                        return null
                                }
                            })}
                        </ul>
                    </div>
                    <div class="mobile-facets__footer ns-only-mobile">
                        {/* Count + Apply button */}
                        {isNewLayout ? (
                            <label 
                                for={toggleButtonId} 
                                class="button button--primary w-full text-center"
                            >
                                Apply // {totalSummary}
                            </label>
                        ) : (
                            <>
                                <p class="mobile-facets__count font-secondary mono-sm">
                                    {totalSummary}
                                </p>
                                <label 
                                    for={toggleButtonId} 
                                    class="button button--primary"
                                >
                                    Apply
                                </label>
                            </>
                        )}

                        {isNewLayout && selectedCount > 0 && (
                            <div class="clear-filters">
                                <button
                                    class="clear-all-button active-facets__button-remove underlined-link font-primary buttons-sm"
                                    role="button"
                                    onClick={() => {
                                        removeAll()
                                    }}>
                                    Clear Filter{selectedCount !== 1 ? 'S' : ''}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    ) : (
        ""
    )
}